<script setup lang="ts">
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiSettingRow from '@/components/pos-v2/ui/PosUiSettingRow.vue';
  import PosUiSettingsCard from '@/components/pos-v2/ui/PosUiSettingsCard.vue';
  import PosUiSheet from '@/components/pos-v2/ui/PosUiSheet.vue';
  import V2DesignColorCard from '@/components/v2/design/V2DesignColorCard.vue';
  import V2DesignCoverCard from '@/components/v2/design/V2DesignCoverCard.vue';
  import V2DesignLookState from '@/components/v2/design/V2DesignLookState.vue';
  import V2DesignModeCard from '@/components/v2/design/V2DesignModeCard.vue';
  import V2DesignPreview from '@/components/v2/design/V2DesignPreview.vue';
  import V2DesignStyleCard from '@/components/v2/design/V2DesignStyleCard.vue';
  import type { ICanvasPageSize } from '@/components/v2/layout/canvas-page';
  import V2CanvasPage from '@/components/v2/layout/V2CanvasPage.vue';
  import { useAssistantStore } from '@/composition/assistant/use-assistant-store';
  import { estimatedPageWidth } from '@/composition/catalog/use-catalog-layout';
  import { useDesignPage } from '@/composition/design/use-design-page';
  import { useStableWindowSize } from '@/composition/use-stable-window-size';
  import { designColumns } from '@/domain/design/design-layout';
  import { previewUrl } from '@/domain/design/preview-message';
  import { Globals } from '@/modules/Globals';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { ignoreNavigationFailure } from '@/utils/navigation.utils';

  /**
   * «Diseño y estilo» (Storefront V2 ADM-3, D-ADM-1): style, colour, mode and
   * cover focal point of the store, with a phone preview of the real store
   * (docked on wide pages, a sheet otherwise) and one save bar.
   */
  const proxy = getCurrentInstance()!.proxy;
  const restaurantStore = useRestaurantStore();
  const assistant = useAssistantStore();
  const { width: windowWidth } = useStableWindowSize();
  const {
    draft,
    saved,
    lookStatus,
    loadLook,
    dirty,
    saving,
    online,
    catalogStatus,
    presetOptions,
    loadCatalog,
    setPreset,
    setBrand,
    setMode,
    setFocal,
    save,
    discard,
  } = useDesignPage();

  /** The canvas width; estimated until the canvas measures itself. */
  const pageWidth = ref(
    estimatedPageWidth(windowWidth.value, assistant.insetRight)
  );
  const measured = ref(false);
  const columns = computed(() =>
    designColumns(pageWidth.value, assistant.insetRight > 0)
  );

  function onCanvasResize(size: ICanvasPageSize) {
    pageWidth.value = size.width;
    measured.value = true;
  }

  const previewOpen = ref(false);
  /**
   * Latched: the store loads in the docked preview once a measured width
   * docks it, or when the owner first opens the sheet; then it stays mounted
   * (a tier change never reloads it).
   */
  const previewMounted = ref(false);
  watch(
    () => measured.value && columns.value.previewDocked,
    (docked) => {
      if (docked) previewMounted.value = true;
    },
    { immediate: true }
  );

  function openPreview() {
    previewMounted.value = true;
    previewOpen.value = true;
  }

  const stringId = computed(() => restaurantStore.restaurant?.stringId ?? '');
  const previewSrc = computed(() =>
    stringId.value ? previewUrl(Globals.website, stringId.value) : ''
  );
  const bannerUrl = computed(() => {
    const banner = restaurantStore.restaurant?.banner;
    return banner ? `${Globals.uploadsPath}/${banner}` : undefined;
  });
  const logoUrl = computed(() => {
    const logo = restaurantStore.restaurant?.logo;
    return logo ? `${Globals.uploadsPath}/${logo}` : undefined;
  });
  const logoColor = computed(
    () => restaurantStore.restaurant?.logoColor ?? undefined
  );
  const mediaPath = computed(() => `/${stringId.value}/settings?tab=basic`);
  const bioPath = computed(() => `/${stringId.value}/settings?tab=info`);

  function go(path: string) {
    proxy.$router.push(path).catch(ignoreNavigationFailure);
  }

  const titleText = ref<HTMLElement | null>(null);
  /**
   * «Reintentar» leaves with its error message: the focus moves first to the
   * title, which stays, so keyboard and screen-reader users keep their place.
   */
  function retryLook() {
    titleText.value?.focus();
    void loadLook();
  }
</script>

<script lang="ts">
  import type { NavigationGuardNext, Route } from 'vue-router';
  import { confirmDesignLeave } from '@/composition/design/use-design-page';

  // vue-router 3 has no composable guards: the in-component one is an option,
  // and it reaches the page through use-design-page.
  export default {
    async beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext) {
      next((await confirmDesignLeave()) ? undefined : false);
    },
  };
</script>

<template>
  <div class="v2-design-page">
    <V2CanvasPage
      :max-width="columns.maxWidth"
      :aside-width="columns.asideWidth"
      :aside-docked="columns.previewDocked"
      :aside-open="true"
      :gutter="columns.gutter"
      :head-surface="columns.tier === 'phone'"
      @resize="onCanvasResize"
    >
      <template #head>
        <div class="design-head">
          <div class="head-title">
            <h1 ref="titleText" class="title-text" tabindex="-1">
              {{ $t('restaurant.menu.store.design') }}
            </h1>
            <p class="title-subtitle">{{ $t('design.subtitle') }}</p>
          </div>
          <PosUiButton
            v-show="!columns.previewDocked"
            class="head-preview"
            color="neutral"
            variant="outline"
            icon="eye-outline"
            :disabled="!draft"
            @click="openPreview"
          >
            {{ $t('design.previewButton') }}
          </PosUiButton>
        </div>
      </template>

      <div v-if="draft && saved" class="design-cards">
        <V2DesignStyleCard
          :value="draft.preset"
          :presets="presetOptions"
          :status="catalogStatus"
          @input="setPreset"
          @retry="loadCatalog"
        />
        <V2DesignColorCard
          :value="draft.brand"
          :saved-brand="saved.brand"
          :logo-color="logoColor"
          @input="setBrand"
        />
        <V2DesignModeCard :value="draft.mode" @input="setMode" />
        <V2DesignCoverCard
          :banner-url="bannerUrl"
          :logo-url="logoUrl"
          :focal="draft.bannerFocal || undefined"
          @input="setFocal"
          @upload="go(mediaPath)"
        />
        <PosUiSettingsCard :title="$t('design.more.title')" flush>
          <PosUiSettingRow
            :label="$t('design.more.media')"
            :hint="$t('design.more.mediaHint')"
          >
            <PosUiButton
              color="neutral"
              variant="outline"
              @click="go(mediaPath)"
            >
              {{ $t('design.more.mediaAction') }}
            </PosUiButton>
          </PosUiSettingRow>
          <PosUiSettingRow
            :label="$t('design.more.bio')"
            :hint="$t('design.more.bioHint')"
          >
            <PosUiButton color="neutral" variant="outline" @click="go(bioPath)">
              {{ $t('design.more.bioAction') }}
            </PosUiButton>
          </PosUiSettingRow>
        </PosUiSettingsCard>
      </div>
      <V2DesignLookState
        v-else
        :failed="lookStatus === 'error'"
        @retry="retryLook"
      />

      <template #aside>
        <PosUiSheet
          class="design-preview-sheet"
          :class="{ 'is-docked': columns.previewDocked }"
          :value="previewOpen"
          :docked="columns.previewDocked"
          :title="$t('design.preview.title')"
          height="lg"
          :max-width="560"
          @input="previewOpen = $event"
        >
          <V2DesignPreview
            v-if="previewMounted && previewSrc"
            :src="previewSrc"
            :draft="draft"
            :docked="columns.previewDocked"
          />
        </PosUiSheet>
      </template>
    </V2CanvasPage>

    <div
      v-show="dirty"
      class="design-save-bar"
      :aria-busy="saving ? 'true' : 'false'"
    >
      <p class="bar-text">{{ $t('design.unsaved') }}</p>
      <div class="bar-actions">
        <PosUiButton
          color="neutral"
          variant="outline"
          :disabled="saving"
          @click="discard"
        >
          {{ $t('design.discard') }}
        </PosUiButton>
        <span
          class="bar-save"
          :title="online ? undefined : $t('design.offline')"
        >
          <PosUiButton
            icon="content-save-outline"
            :disabled="saving || !online"
            @click="save"
          >
            {{ $t('design.save') }}
          </PosUiButton>
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-design-page {
    @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col;

    .design-head {
      @apply tw-flex tw-items-start tw-justify-between tw-pb-3;
    }

    .head-title {
      @apply tw-mr-4 tw-min-w-0 tw-flex-1;
    }

    .title-text {
      @apply tw-m-0 tw-text-2xl tw-font-semibold tw-leading-[30px] tw-text-neutral-900 tw-outline-none;
    }

    .title-subtitle {
      @apply tw-m-0 tw-mt-0.5 tw-text-[13px] tw-leading-5 tw-text-neutral-500;
    }

    .head-preview {
      @apply tw-flex-none;
    }

    .design-cards {
      @apply tw-space-y-3 tw-pb-6;
    }

    // The docked panel fills the aside's fixed height, so the preview has a
    // definite box to fit the phone into.
    .design-preview-sheet.is-docked {
      @apply tw-h-full;
    }

    // In the page's column below the canvas: it shrinks the canvas instead
    // of covering it. On phones the text gets its own line above the
    // buttons, so it is never cut in any language; wider, one row.
    .design-save-bar {
      @apply tw-flex tw-shrink-0 tw-flex-col tw-border-0 tw-border-t tw-border-solid tw-border-neutral-200 tw-bg-surface tw-px-4 tw-py-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between md:tw-px-6;
      padding-bottom: calc(12px + env(safe-area-inset-bottom));
    }

    .bar-text {
      @apply tw-m-0 tw-text-sm tw-font-medium tw-text-neutral-700 sm:tw-mr-3 sm:tw-min-w-0 sm:tw-flex-1;
    }

    .bar-actions {
      @apply tw-mt-2 tw-flex tw-flex-none tw-items-center tw-justify-end tw-space-x-2 sm:tw-mt-0;
    }
  }
</style>
